import { memo, useId, type CSSProperties, type ReactElement } from "react";
import { marpeleXY } from "../board/state";
import { fa } from "./cards";
import { ICON_ART, LADDER_ART, SNAKE_SHAPES } from "./marpeleArtData";
import { CAR_ART } from "./marpeleCarData";

/**
 * تخته‌ی مارپله: خانه‌ها، نردبان‌های چوبی و مارها. همه با کد کشیده می‌شوند، چون هر
 * مسابقه چیدمانِ خودش را دارد (سرور می‌سازد، در extra می‌فرستد) و سر و دُمِ هر مار و دو سرِ
 * هر نردبان باید دقیقاً روی خانه‌ی همان چیدمان بنشیند.
 *
 * مار، پله و نشان‌ها آرتِ کاربرند (۲۶ اکتبر ۲۰۲۶، «روشِ ورق» ِ docs/snake-art-prompts.md؛ بُرش و شش رنگ با
 * tools/marpele_art_cut.py، اندازه‌ها در marpeleArtData.ts): هر مار یک تصویرِ کامل است — سر بالا، تنه‌ی S و پیچِ دم پایین —
 * که از خانه‌ی سر تا خانه‌ی دم کشیده و چرخانده می‌شود (snakeFit) — سرش گرد و کوچک می‌ماند، فقط تن کشیده می‌شود؛ از سه درازا نزدیک‌ترین برداشته و یکی در میان آینه
 * می‌شود. پله از کلاهِ بالا، پله‌های تکراری و کلاهِ پایین ساخته می‌شود تا هر درازایی پله‌های هم‌فاصله داشته باشد. مهره
 * روی مار از خطِ میانه‌ی همان آرت پایین سُر می‌خورد (jumpPath).
 *
 * شکل و رنگِ هر مار فقط از چیدمان می‌آید (snakeLook): شماره‌ی مار در فهرستِ سرها از بالا
 * به پایین، و طولش. پس اپ (UI/SnakeView.cs، همین فرمول‌ها) و وب یک تخته را یکسان می‌کشند.
 *
 * واحد = یک خانه؛ خانه‌ی ۱ پایین-چپ، ۱۰۰ بالا-چپ، y رو به پایین (marpeleXY).
 */

export type V = [number, number];
export type Jumps = Readonly<Record<number, number>>;

export interface SnakeLook { palette: number }

/** تنه، تیره (خطِ دور و نقش)، روشن (برق). همان SnakeView.Palettes. */
export const SNAKE_PALETTES: ReadonlyArray<{ base: string; dark: string; light: string }> = [
  { base: "#45a64a", dark: "#2b6a2f", light: "#c2eca2" },   // سبز
  { base: "#ee8a3d", dark: "#a3521c", light: "#ffdcae" },   // نارنجی
  { base: "#8f64ca", dark: "#5a3d8b", light: "#e2d2fa" },   // بنفش
  { base: "#27a39f", dark: "#186a68", light: "#c3f2ea" },   // فیروزه‌ای
  { base: "#da4a3c", dark: "#8f2921", light: "#ffc8ba" },   // قرمز
  { base: "#e6b530", dark: "#94700f", light: "#fff1bb" },   // زرد
];

/** تمِ تخته — هر مسابقه یکی (سرور می‌گزیند). ترتیب همان marpele.Themes ِ سرور و SnakeArt.Themes ِ اپ. */
export interface BoardTheme {
  name: string;
  frame: [string, string]; edge: string; grout: string;
  cells: [string, string]; start: string; goal: [string, string]; num: string;
  rail: string; railHi: string; rung: string;
}
export const THEMES: ReadonlyArray<BoardTheme> = [
  { name: "باغ ایرانی", frame: ["#2f6b5a", "#1b4639"], edge: "#d4b25f", grout: "#b8d3c1", cells: ["#f7f3e3", "#dbeede"],
    start: "#cfe7f0", goal: ["#f8dc84", "#e2b347"], num: "#3f5a4a", rail: "#b07539", railHi: "#e6b777", rung: "#86552a" },
  { name: "بازار", frame: ["#855429", "#5a3719"], edge: "#b98245", grout: "#d6bf92", cells: ["#f8f0dd", "#efdcb5"],
    start: "#d9eee5", goal: ["#f8dc84", "#e2b347"], num: "#6d5536", rail: "#b07539", railHi: "#e6b777", rung: "#86552a" },
  { name: "کویر", frame: ["#b3803f", "#7a5024"], edge: "#f0cf8a", grout: "#e2c794", cells: ["#fdf5de", "#f2dca9"],
    start: "#d6ebf4", goal: ["#f8dc84", "#e2b347"], num: "#7a5a2a", rail: "#8d6c4b", railHi: "#dcbd96", rung: "#6b4e33" },
  { name: "جنگل شمال", frame: ["#3f5a2e", "#24371a"], edge: "#b9c97a", grout: "#a8c39a", cells: ["#f1f5e6", "#d4e5c3"],
    start: "#d7ecf2", goal: ["#f8dc84", "#e2b347"], num: "#435a36", rail: "#9a6b3c", railHi: "#d9ae78", rung: "#6f4a26" },
];
export const themeOf = (i: number): BoardTheme => THEMES[((i % THEMES.length) + THEMES.length) % THEMES.length]!;

/** رنگ و نامِ خانه‌های جادویی؛ نوع ۱ پادزهر، ۲ شمشیر، ۳ تاسِ دوباره، ۴ جابه‌جایی، ۵ ایست، ۶ ماشین. */
export const MAGIC_LOOK: Readonly<Record<number, { color: string; name: string }>> = {
  1: { color: "#34b86a", name: "پادزهر" },
  2: { color: "#4f86e8", name: "شمشیر" },
  3: { color: "#efb02c", name: "تاس دوباره" },
  4: { color: "#a35be0", name: "جابه‌جایی با نفر اول" },
  5: { color: "#e2503a", name: "ایست" },
  6: { color: "#f07a22", name: "ماشین" },
};

/** نشانِ هر نیرو در یک مربعِ واحد به مرکزِ ۰ — سفید، روی رنگِ خانه یا پلاک. */
export function MagicIcon({ kind, x = 0, y = 0, s = 1, fill = "#fff" }: { kind: number; x?: number; y?: number; s?: number; fill?: string }): ReactElement {
  const body = (() => {
    switch (kind) {
      case 1: return (<g fill={fill}><rect x={-0.15} y={-0.02} width={0.3} height={0.3} rx={0.09} /><rect x={-0.06} y={-0.2} width={0.12} height={0.2} /><rect x={-0.09} y={-0.27} width={0.18} height={0.08} rx={0.03} /></g>);
      case 2: return (<g stroke={fill} strokeLinecap="round" fill="none"><path d="M-0.2,0.2 L0.2,-0.2" strokeWidth={0.075} /><path d="M-0.17,0.05 L-0.05,0.17" strokeWidth={0.06} /><circle cx={-0.23} cy={0.23} r={0.035} fill={fill} stroke="none" /></g>);
      case 3: return (<g><rect x={-0.19} y={-0.19} width={0.38} height={0.38} rx={0.08} fill={fill} /><circle cx={-0.08} cy={-0.08} r={0.04} fill="#555" /><circle cx={0.08} cy={0.08} r={0.04} fill="#555" /></g>);
      case 4: return (<g stroke={fill} strokeWidth={0.06} strokeLinecap="round" strokeLinejoin="round" fill="none"><path d="M-0.2,-0.07 L0.18,-0.07 M0.09,-0.16 L0.18,-0.07 L0.09,0.02" /><path d="M0.2,0.09 L-0.18,0.09 M-0.09,0 L-0.18,0.09 L-0.09,0.18" /></g>);
      case 5: return (<g stroke={fill} strokeWidth={0.06} fill="none"><circle r={0.2} /><path d="M-0.14,0.14 L0.14,-0.14" strokeLinecap="round" /></g>);
    }
    return <g />;
  })();
  return <g transform={`translate(${x} ${y}) scale(${s})`}>{body}</g>;
}

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1]];
const len = (a: V): number => Math.hypot(a[0], a[1]);
const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

/** سرِ مارهای چیدمان، از بالای تخته به پایین — شماره‌ی هر مار در همین فهرست است. */
export function snakeHeads(jumps: Jumps): number[] {
  return Object.keys(jumps).map(Number).filter((h) => jumps[h]! < h).sort((a, b) => b - a);
}

/** رنگِ مارِ شماره‌ی index — همان ترتیبِ SNAKE_PALETTES و رنگِ آرتِ همان شماره. */
export function snakeLook(_head: number, _tail: number, index: number): SnakeLook {
  return { palette: ((index % SNAKE_PALETTES.length) + SNAKE_PALETTES.length) % SNAKE_PALETTES.length };
}

const ART = "/art/marpele/";
type Shape = (typeof SNAKE_SHAPES)[number];
const deg = (v: V): number => (Math.atan2(v[1], v[0]) * 180) / Math.PI;
const rot = (v: V, d: number): V => {
  const r = (d * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  return [v[0] * c - v[1] * s, v[0] * s + v[1] * c];
};
const axisOf = (s: Shape): V => [s.tail[0] - s.head[0], s.tail[1] - s.head[1]];
/**
 * پهنای سرِ مار روی تخته (خانه): خوش‌اندازه ۰٫۷۵ و هرگز بیش از ۰٫۸۵ (کاربر، ۲۶ اکتبر ۲۰۲۶: «سرِ مار کمی کوچک‌تر»)؛ مارِ
 * خیلی بلند سرِ بزرگ‌تر نمی‌گیرد، تنش درازتر می‌شود.
 */
const HEAD_NICE = 0.75, HEAD_MIN = 0.55, HEAD_MAX = 0.85;
/** گردن، ضربدرِ پهنای سر زیرِ مرکزِ سر: بالایش (سر و زبان) بی‌کشیدگی کشیده می‌شود تا سر گرد بماند، پایینش تا دم کشیده. */
const NECK = 0.75;
/** درازایی (خانه) که این آرت با سرِ خوش‌اندازه دارد. */
const natural = (s: Shape): number => (len(axisOf(s)) * HEAD_NICE) / s.headW;

/**
 * یک مارِ آرت روی تخته: کدام تصویر، دو تبدیلِ SVG از پیکسلِ آرت به خانه (سر تا ردیفِ گردن بی‌کشیدگی، تن از گردن تا دم
 * کشیده)، پهنای سر (خانه) و خطِ میانه‌اش روی تخته از سر تا دم.
 */
export interface SnakeFit { shape: Shape; color: number; headTf: string; bodyTf: string; neckRow: number; size: number; path: V[]; head: V }

/**
 * مارِ آرت میانِ خانه‌ی سر و خانه‌ی دم: مرکزِ سرِ آرت روی خانه‌ی سر و مرکزِ پیچِ دم روی خانه‌ی دم. درازا از سه آرت نزدیک‌ترین
 * به این فاصله؛ سر در [HEAD_MIN، HEAD_MAX] و گرد، تن از گردن تا دم همان‌قدر کشیده که به دم برسد. یکی در میان آینه.
 * اپ (SnakeArt.FitSnake) همین را حساب می‌کند.
 */
export function snakeFit(head: number, tail: number, index: number): SnakeFit {
  const H = marpeleXY(head), T = marpeleXY(tail);
  const B = sub(T, H), D = len(B) || 1;
  const shape = SNAKE_SHAPES.reduce((best, c) =>
    Math.abs(Math.log(D / natural(c))) < Math.abs(Math.log(D / natural(best))) ? c : best, SNAKE_SHAPES[0] as Shape);
  const A = axisOf(shape), a = len(A), n = NECK * shape.headW;
  const size = clamp((shape.headW * D) / a, HEAD_MIN, HEAD_MAX);
  const s = size / shape.headW, flip = index % 2 === 1 ? -1 : 1;
  // تن: از گردن (n*s زیرِ سر) تا دم (D)
  const along = (D - n * s) / (a - n);
  const angA = deg(A), angB = deg(B);
  const map = (p: readonly number[]): V => {
    const q = rot([p[0]! - shape.head[0], p[1]! - shape.head[1]], 90 - angA);
    const r = rot([q[0] * s * flip, q[1] < n ? q[1] * s : n * s + (q[1] - n) * along], angB - 90);
    return [r[0] + H[0], r[1] + H[1]];
  };
  const path = shape.line.map(map);
  path[0] = H; path[path.length - 1] = T;
  const frame = `translate(${H[0].toFixed(3)} ${H[1].toFixed(3)}) rotate(${(angB - 90).toFixed(2)})`;
  const art = `rotate(${(90 - angA).toFixed(2)}) translate(${-shape.head[0]} ${-shape.head[1]})`;
  return {
    shape, color: snakeLook(head, tail, index).palette, head: H, path, size, neckRow: shape.head[1] + n,
    headTf: `${frame} scale(${(s * flip).toFixed(5)} ${s.toFixed(5)}) ${art}`,
    bodyTf: `${frame} translate(0 ${(n * s).toFixed(4)}) scale(${(s * flip).toFixed(5)} ${along.toFixed(5)}) translate(0 ${-n}) ${art}`,
  };
}

export const snakeSrc = (f: SnakeFit, state: "calm" | "bite" | "dead" = "calm"): string =>
  `${ART}${f.shape.key}-${state === "dead" ? "dead" : `${f.color}${state === "bite" ? "-bite" : ""}`}.webp`;

/**
 * تصویرِ مار در دو تکه از همان فایل: تن از ردیفِ گردن به پایین با تبدیلِ کشیده، سر تا گردن (و دو پیکسل بیشتر، روی تن) با تبدیلِ
 * گرد. سایه‌اش با فیلترِ shadow روی هر دو.
 */
export function SnakeImage({ fit, src, shadow }: { fit: SnakeFit; src: string; shadow?: string }): ReactElement {
  const { w, h } = fit.shape, cut = fit.neckRow, top = cut + 2;
  const img = <image href={src} width={w} height={h} preserveAspectRatio="none" />;
  return (
    <g filter={shadow}>
      <g transform={fit.bodyTf}><svg x={0} y={cut} width={w} height={h - cut} viewBox={`0 ${cut} ${w} ${h - cut}`} overflow="hidden">{img}</svg></g>
      <g transform={fit.headTf}><svg x={0} y={0} width={w} height={top} viewBox={`0 0 ${w} ${top}`} overflow="hidden">{img}</svg></g>
    </g>
  );
}

function Snake({ head, tail, index, shadow, dead }: { head: number; tail: number; index: number; shadow: string; dead: boolean }): ReactElement {
  const fit = snakeFit(head, tail, index);
  const src = snakeSrc(fit, dead ? "dead" : "calm");
  return (
    <g data-head={head} opacity={dead ? 0.8 : 1}>
      <g transform="translate(0.07 0.09)"><SnakeImage fit={fit} src={src} shadow={shadow} /></g>
      <SnakeImage fit={fit} src={src} />
    </g>
  );
}

/** پله‌ی آرت از پایه تا سر: کلاهِ بالا، n پله‌ی تکراری، کلاهِ پایین؛ پهنا نیم خانه و درازا کمی بیرون از مرکزِ دو خانه. */
function Ladder({ from, to, shadow }: { from: number; to: number; shadow: string }): ReactElement {
  const A = marpeleXY(from), B = marpeleXY(to);
  const d = sub(B, A), L0 = len(d) || 1;
  const u = 0.5 / LADDER_ART.w;
  const L = L0 + 0.3;
  const n = Math.max(1, Math.round((L / u - LADDER_ART.top - LADDER_ART.bottom) / LADDER_ART.rung));
  const along = L / (LADDER_ART.top + n * LADDER_ART.rung + LADDER_ART.bottom);
  const top: V = [B[0] + (d[0] / L0) * 0.15, B[1] + (d[1] / L0) * 0.15];
  const tf = `translate(${top[0].toFixed(3)} ${top[1].toFixed(3)}) rotate(${(deg([-d[0], -d[1]]) - 90).toFixed(2)}) scale(${u.toFixed(5)} ${along.toFixed(5)}) translate(${-LADDER_ART.w / 2} 0)`;
  // تکه‌ها کمی روی هم: درزِ نیم‌پیکسلیِ لبه‌ی تصویرها پیدا نشود
  const parts: ReactElement[] = [<image key="t" href={`${ART}ladder-top.webp`} y={0} width={LADDER_ART.w} height={LADDER_ART.top + 1} preserveAspectRatio="none" />];
  for (let k = 0; k < n; k++)
    parts.push(<image key={k} href={`${ART}ladder-rung.webp`} y={LADDER_ART.top + k * LADDER_ART.rung} width={LADDER_ART.w} height={LADDER_ART.rung + 1} preserveAspectRatio="none" />);
  parts.push(<image key="b" href={`${ART}ladder-bottom.webp`} y={LADDER_ART.top + n * LADDER_ART.rung} width={LADDER_ART.w} height={LADDER_ART.bottom} preserveAspectRatio="none" />);
  return (
    <g data-ladder={from}>
      <g transform="translate(0.06 0.08)" filter={shadow}><g transform={tf}>{parts}</g></g>
      <g transform={tf}>{parts}</g>
    </g>
  );
}

/** نشانِ آرت (کاربر) در جعبه‌ی s×s به مرکزِ (x، y)، با همان نسبتِ خودش. */
export function IconArt({ name, x = 0, y = 0, s }: { name: keyof typeof ICON_ART; x?: number; y?: number; s: number }): ReactElement {
  const [w, h] = ICON_ART[name];
  const k = s / Math.max(w, h);
  return <image href={`${ART}${name}.webp`} x={x - (w * k) / 2} y={y - (h * k) / 2} width={w * k} height={h * k} preserveAspectRatio="none" />;
}

/** نشانِ نیرو: پادزهر، شمشیر، تاس و ماشین از آرت؛ جابه‌جایی و ایست هنوز کدی، به رنگِ خودشان. */
const MAGIC_ART: Readonly<Record<number, keyof typeof ICON_ART>> = { 1: "potion", 2: "sword", 3: "die" };
export function MagicArt({ kind, s, fill }: { kind: number; s: number; fill?: string }): ReactElement {
  if (kind === 6) {
    const [w, h] = CAR_ART.icon, k = s / Math.max(w, h);
    return <image href={`${ART}car.webp`} x={(-w * k) / 2} y={(-h * k) / 2} width={w * k} height={h * k} preserveAspectRatio="none" />;
  }
  const art = MAGIC_ART[kind];
  return art ? <IconArt name={art} s={s} /> : <MagicIcon kind={kind} s={s * 1.6} fill={fill ?? MAGIC_LOOK[kind]?.color ?? "#fff"} />;
}

/** پهنای ماشین و راننده به ضلعِ خانه — همان CarBody ِ اپ. */
export const CAR_W = 1.5;

/**
 * ماشینِ سوار (کاربر، ۲۶ اکتبر ۲۰۲۶؛ آرتِ tools/marpele_car_cut.py): پسر یا دختر به رنگِ آرتِ ci، وسطِ زیرِ چرخ‌ها روی
 * (۰، ۰)؛ آرت رو به راست است و رو به چپ را صدا‌زننده آینه می‌کند. bump: لحظه‌ی کوبیدن، روی چرخِ عقب.
 */
export function CarArt({ ci, girl, bump }: { ci: number; girl: boolean; bump?: boolean }): ReactElement {
  const k = CAR_W / CAR_ART.body;
  return (
    <image href={`${ART}car-${girl ? "g" : "p"}${ci}${bump ? "-bump" : ""}.webp`} x={-CAR_ART.ax * k} y={-CAR_ART.ay * k}
           width={CAR_ART.w * k} height={CAR_ART.h * k} preserveAspectRatio="none" />
  );
}

/** کلیدِ چیدمان — تخته فقط وقتی از نو کشیده می‌شود که چیدمان، تم یا مارهای مرده عوض شده باشند. */
const layoutKey = (j: Jumps): string => Object.keys(j).map(Number).sort((a, b) => a - b).map((a) => `${a}:${j[a]}`).join(",");

/** بخشِ ایستای تخته — قاب، خانه‌ها، نردبان‌ها، مارها. با حرکتِ مهره از نو ساخته نمی‌شود. */
export const MarpeleBoardArt = memo(function MarpeleBoardArt({ jumps, theme = 1, dead = [] }: { jumps: Jumps; theme?: number; dead?: readonly number[] }): ReactElement {
  const uid = useId().replace(/:/g, "");
  const th = themeOf(theme);
  const cells: ReactElement[] = [];
  for (let n = 1; n <= 100; n++) {
    const [x, y] = marpeleXY(n);
    const cx = Math.floor(x), cy = Math.floor(y);
    const fill = n === 100 ? `url(#gold${uid})` : n === 1 ? th.start : (cx + cy) % 2 === 0 ? th.cells[0] : th.cells[1];
    cells.push(
      <g key={n}>
        <rect x={cx + 0.025} y={cy + 0.025} width={0.95} height={0.95} rx={0.08} fill={fill} />
        <text x={cx + 0.1} y={cy + 0.3} fontSize={0.25} fontWeight={700} fill={th.num} fillOpacity={0.62} direction="ltr">{fa(n)}</text>
      </g>,
    );
  }
  const ladders: ReactElement[] = [];
  for (const [fromS, to] of Object.entries(jumps)) {
    const from = Number(fromS);
    if (to > from) ladders.push(<Ladder key={from} from={from} to={to} shadow={`url(#sh${uid})`} />);
  }
  // مارهای بلند زیر، کوتاه‌ها رو: سرِ همه دیده شود
  const heads = snakeHeads(jumps);
  const order = heads.map((h, index) => ({ h, index, L: len(sub(marpeleXY(h), marpeleXY(jumps[h]!))) }))
    .sort((p, q) => q.L - p.L);
  const snakes = order.map(({ h, index }) => <Snake key={h} head={h} tail={jumps[h]!} index={index} shadow={`url(#sh${uid})`} dead={dead.includes(h)} />);
  const [gx, gy] = marpeleXY(100), [sx, sy] = marpeleXY(1);
  return (
    <g>
      <defs>
        <linearGradient id={`wood${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={th.frame[0]} /><stop offset="1" stopColor={th.frame[1]} />
        </linearGradient>
        <linearGradient id={`gold${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={th.goal[0]} /><stop offset="1" stopColor={th.goal[1]} />
        </linearGradient>
        {/* سایه‌ی مار و پله: همان شکل، سیاهِ کم‌رنگ */}
        <filter id={`sh${uid}`} x="-10%" y="-10%" width="120%" height="120%">
          <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.22 0" />
        </filter>
      </defs>
      <rect x={-0.2} y={-0.2} width={10.4} height={10.4} rx={0.38} fill={`url(#wood${uid})`} />
      <rect x={-0.12} y={-0.12} width={10.24} height={10.24} rx={0.3} fill="none" stroke={th.edge} strokeWidth={0.035} opacity={0.7} />
      <rect x={0} y={0} width={10} height={10} rx={0.12} fill={th.grout} />
      {cells}
      {ladders}
      <IconArt name="crown" x={gx} y={gy + 0.06} s={0.62} />
      <IconArt name="flag" x={sx + 0.1} y={sy + 0.04} s={0.56} />
      {snakes}
    </g>
  );
}, (a, b) => layoutKey(a.jumps) === layoutKey(b.jumps) && a.theme === b.theme && (a.dead ?? []).join() === (b.dead ?? []).join());

/**
 * خانه‌های جادوییِ همین لحظه — جدا از تخته‌ی ایستا، چون با هر تاس جا عوض می‌کنند. هر
 * کدام رنگِ نوعِ خودش را دارد و چشمک می‌زند (CSS)؛ جای تازه با «پاپ» پیدا می‌شود.
 */
export function MarpeleMagics({ magics }: { magics: readonly { cell: number; kind: number }[] }): ReactElement {
  return (
    <g>
      {magics.map((m) => {
        const [x, y] = marpeleXY(m.cell);
        const look = MAGIC_LOOK[m.kind];
        if (!look) return null;
        return (
          <g key={`${m.cell}:${m.kind}`} className={`mp-magic k${m.kind}`} transform={`translate(${x} ${y})`}>
            <g className="mp-magic-in">
              <rect x={-0.44} y={-0.44} width={0.88} height={0.88} rx={0.14} fill={look.color} opacity={0.3} />
              <rect x={-0.34} y={-0.34} width={0.68} height={0.68} rx={0.12} fill="#fffaf0" stroke={look.color} strokeWidth={0.06} />
              <MagicArt kind={m.kind} s={0.56} />
            </g>
          </g>
        );
      })}
    </g>
  );
}

/** سرِ مارهایی که با تاسِ همین نوبت در دسترس‌اند — حلقه‌ی قرمزِ تپنده، تا تعلیقِ تاس بیشتر شود. */
export function MarpeleDanger({ heads }: { heads: readonly number[] }): ReactElement {
  return (
    <g pointerEvents="none">
      {heads.map((h) => {
        const [x, y] = marpeleXY(h);
        return <circle key={h} className="mp-danger" cx={x} cy={y} r={0.42} fill="none" stroke="#e2503a" strokeWidth={0.06} />;
      })}
    </g>
  );
}

/** راهِ مهره روی پرش: روی بدنِ مار از سر تا دُم، یا راست از پایه تا سرِ نردبان. */
export function jumpPath(jumps: Jumps, from: number, to: number): V[] {
  if (jumps[from] !== to || to > from) return [marpeleXY(from), marpeleXY(to)];
  return snakeFit(from, to, snakeHeads(jumps).indexOf(from)).path;
}

/** سرِ نیش‌زننده‌ی یک مار (همان تبدیل، تصویرِ دهان‌باز) — برای لحظه‌ی نیش (MarpeleFx). */
export function snakeBite(jumps: Jumps, head: number): { fit: SnakeFit; src: string } | null {
  const tail = jumps[head], index = snakeHeads(jumps).indexOf(head);
  if (tail === undefined || tail > head || index < 0) return null;
  const fit = snakeFit(head, tail, index);
  return { fit, src: snakeSrc(fit, "bite") };
}

/** لولای جهشِ سرِ نیش‌زننده روی مرکزِ سر (CSS ِ .mp-bite-head). */
export const biteOrigin = (fit: SnakeFit): CSSProperties => ({ transformOrigin: `${fit.head[0]}px ${fit.head[1]}px` });
